import { FC } from 'react';
import { Control, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Label, Larger } from '@/components/Forms';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';

const Radio = styled.div<{ $active: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  transition: 0.15s ease-out border-color;
  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
      position: relative;
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        margin: auto;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${theme.colors.purple};
      }
    `}
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  height: 46px;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  width: 100%;

  &:hover {
    ${Radio} {
      border-color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const options = [
  {
    value: 'prepend',
    label: 'Prepend new posts to the start',
  },
  {
    value: 'append',
    label: 'Append new posts to the end',
  },
  {
    value: 'override',
    label: 'Override current list',
  },
  {
    value: 'instant',
    label: 'Instant post',
  },
];

interface Props {
  control: Control<any>;
}

const PostFormOptionsField: FC<Props> = ({ control }) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    name: 'option',
  });

  return (
    <Larger>
      <Label>Select option:</Label>
      <Options>
        {options.map(({ value: val, label }) => (
          <OptionButton key={val} onClick={() => onChange(val)}>
            <Radio $active={val === value} />
            <Text as="span" $type="captionSemibold">
              {label}
            </Text>
          </OptionButton>
        ))}
      </Options>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </Larger>
  );
};

export default PostFormOptionsField;
